import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Button, Container, Box, IconButton, Drawer, List, ListItem, ListItemText, useScrollTrigger } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';
import { useStateValue } from '../../../contexto/store';
import { LogoIcon } from '../../tool';
import { useInstitutions } from '../../../hooks/useInstitutions';

const ElevationScroll = (props) => {
  const { children } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return React.cloneElement(children, {
    elevation: trigger ? 4 : 0,
    sx: {
      backgroundColor: trigger ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.85)',
      backdropFilter: 'blur(10px)',
      color: 'primary.main',
      transition: 'all 0.3s ease-in-out',
      borderBottom: trigger ? '1px solid rgba(0, 105, 92, 0.2)' : 'none',
      textShadow: 'none',
    },
  });
};

const LandingHeader = () => {
  const navigate = useNavigate();
  const [{ sesionUsuario }] = useStateValue();
  const { institutions } = useInstitutions();
  const institution = institutions && institutions.length > 0 ? institutions[0] : null;

  const [mobileOpen, setMobileOpen] = useState(false);
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });
  const isAuthenticated = sesionUsuario ? sesionUsuario.autenticado : false;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = [
    { label: 'Inicio', id: 'hero' },
    { label: 'Servicios', id: 'features' },
    { label: 'Nosotros', id: 'about' },
    { label: 'Estadísticas', id: 'stats' },
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  return (
    <>
      <ElevationScroll>
        <AppBar position="fixed">
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => scrollToSection('hero')}>
                {institution?.logo ? (
                  <Box
                    component="img"
                    src={institution.logo}
                    alt="Logo"
                    sx={{ width: 45, height: 45, mr: 2, objectFit: 'contain' }}
                  />
                ) : (
                  <LogoIcon sx={{ fontSize: 45, mr: 2, color: 'primary.main', border: '1px solid #00695c' }} />
                )}
                <Typography
                  variant="h6"
                  noWrap
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '.1rem',
                    color: 'inherit',
                    textDecoration: 'none',
                    display: { xs: 'none', sm: 'block' }
                  }}
                >
                  {institution?.name || "AGUA POTABLE"}
                </Typography>
              </Box>

              {/* Desktop Menu */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
                {navItems.map((item) => (
                  <Button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    sx={{ color: 'inherit', fontWeight: 600, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                  >
                    {item.label}
                  </Button>
                ))}
                <Button
                  variant={isAuthenticated ? "outlined" : "contained"}
                  color={isAuthenticated ? "inherit" : "secondary"}
                  onClick={() => navigate(isAuthenticated ? "/dashboard" : "/auth/login")}
                  sx={{
                    ml: 2,
                    borderRadius: '20px',
                    px: 3,
                    fontWeight: 'bold',
                    boxShadow: isAuthenticated ? 'none' : '0 4px 14px 0 rgba(0,105,92,0.39)',
                    '&:hover': {
                      bgcolor: isAuthenticated ? 'rgba(255,255,255,0.1)' : 'secondary.dark',
                    }
                  }}
                >
                  {isAuthenticated ? 'MI PANEL' : 'INGRESAR'}
                </Button>
              </Box>

              {/* Mobile Menu Icon */}
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ display: { md: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
            </Toolbar>
          </Container>
        </AppBar>
      </ElevationScroll>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240, backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)' },
        }}
      >
        <Box sx={{ textAlign: 'center', p: 2 }}>
          <Typography variant="h6" sx={{ my: 2, fontWeight: 700, color: 'primary.main' }}>
            MENU
          </Typography>
          <List>
            {navItems.map((item) => (
              <ListItem key={item.id} disablePadding onClick={() => scrollToSection(item.id)}>
                <ListItemText primary={item.label} sx={{ textAlign: 'center', color: 'text.primary' }} />
              </ListItem>
            ))}
            <ListItem disablePadding>
              <Button
                fullWidth
                variant="contained"
                sx={{ m: 2 }}
                onClick={() => navigate(isAuthenticated ? "/dashboard" : "/auth/login")}
              >
                {isAuthenticated ? 'MI PANEL' : 'INGRESAR'}
              </Button>
            </ListItem>
          </List>
        </Box>
      </Drawer>
      <Toolbar /> {/* Spacer */}
    </>
  );
};

export default LandingHeader;
